import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
} from 'react'
import { useNavigate } from 'react-router'
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js'
import { searchGlobal, type SearchResults } from '../../api/search.js'
import { useSavedSearches } from '../../hooks/useSavedSearches.js'
import { EmptyState } from '../data/EmptyState.js'
import {
  ResultSections,
  flattenResults,
  applyScope,
  type ResultScope,
} from './ResultSections.js'

interface ChipDef {
  scope: ResultScope
  label: string
}

const SCOPE_CHIPS: readonly ChipDef[] = [
  { scope: 'all', label: 'All' },
  { scope: 'conversations', label: 'Conversations' },
  { scope: 'contacts', label: 'Contacts' },
  { scope: 'calls', label: 'Calls' },
  { scope: 'recordings', label: 'Recordings' },
  { scope: 'agents', label: 'Agents' },
  { scope: 'messages', label: 'Messages' },
] as const

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250

/**
 * Global Cmd-K / Ctrl-K search palette mounted once at the App root. Listens for:
 *   - keydown with metaKey/ctrlKey + 'k' → opens
 *   - window CustomEvent('open-search') → opens (used by the visible search
 *     button in admin/platform layouts)
 *   - Escape (while open) → closes
 *
 * Debounces the input by 250 ms before calling /search (handled in api/search).
 * Short queries (< 2 chars) render a hint. Zero results render <EmptyState />.
 *
 * UI-9d Task 10 — Mobile-friendly modal sizing.
 *
 * On phone the overlay opens near the top of the viewport (`pt-2`) and
 * the panel spans the full screen width minus a small inset
 * (`mx-2`). On `lg+` the original centered desktop modal returns
 * (`lg:pt-24 lg:mx-auto lg:max-w-2xl`). The results panel scrolls
 * (`max-h-[70vh] overflow-y-auto`) so a tall result set never pushes
 * content off-screen on phone.
 *
 * Phase-2 Batch B2 — Saved searches.
 *
 * A small "Saved Searches" strip renders above the input when the
 * current user has at least one saved search. Clicking a chip loads
 * its `queryString` into the input. A trailing × button on each chip
 * deletes the row via the useSavedSearches hook. The "Save" button
 * appears next to the input once the live query is >= MIN_QUERY_LENGTH;
 * pressing it persists the current query under a name read from a
 * lightweight inline prompt so we don't ship a heavy modal for a 1-line
 * input.
 */
export function SearchPalette(): JSX.Element | null {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [scope, setScope] = useState<ResultScope>('all')
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS)

  // Hook is mounted but its initial fetch only fires while the palette
  // is open in practice — useAdminResource still issues the GET on
  // mount, which is fine because the route is cheap and the list is
  // tiny.
  const saved = useSavedSearches()

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setResults(null)
    setActiveIndex(0)
    setScope('all')
  }, [])

  // Global keyboard listener for Cmd-K / Ctrl-K
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent): void {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      if (isCmdK) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Custom event listener — the visible search button dispatches this so it
  // can open the palette without sharing React state.
  useEffect(() => {
    function onOpen(): void {
      setOpen(true)
    }
    window.addEventListener('open-search', onOpen)
    return () => window.removeEventListener('open-search', onOpen)
  }, [])

  // Auto-focus the input when the palette opens.
  useEffect(() => {
    if (open) {
      // Defer one tick so jsdom / React commits the input before .focus().
      const id = window.setTimeout(() => inputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
    return undefined
  }, [open])

  // Run the search when the debounced query passes the min length.
  useEffect(() => {
    if (!open) return
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setResults(null)
      setActiveIndex(0)
      return
    }
    let cancelled = false
    void searchGlobal(debouncedQuery)
      .then((res) => {
        if (!cancelled) {
          setResults(res)
          setActiveIndex(0)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults(null)
          setActiveIndex(0)
        }
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, open])

  const handleSelect = useCallback(
    (href: string) => {
      navigate(href)
      close()
    },
    [navigate, close],
  )

  const loadSaved = useCallback((queryString: string) => {
    setQuery(queryString)
    // Refocus the input so the user can continue typing immediately
    // after picking a saved search.
    inputRef.current?.focus()
  }, [])

  const handleSave = useCallback(async () => {
    if (query.length < MIN_QUERY_LENGTH) return
    // window.prompt is intentional here — the saved-search create flow
    // takes one short string (the display name) and shipping a modal +
    // form for one input would be far heavier than the user value
    // warrants. The prompt is dismissable; an empty/cancelled response
    // skips the create.
    const name = window.prompt('Name this saved search', query)
    if (!name || name.trim().length === 0) return
    try {
      await saved.create(name.trim(), query)
    } catch {
      // Errors (e.g. 409 SAVED_SEARCH_EXISTS) surface via the hook's
      // refetch path — we intentionally swallow here to keep the
      // prompt-driven UX simple. A future iteration can layer a toast.
    }
  }, [query, saved])

  const handleDeleteSaved = useCallback(
    async (id: string) => {
      try {
        await saved.remove(id)
      } catch {
        // Silent — see handleSave note.
      }
    },
    [saved],
  )

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    // Use the SCOPED flat list so Enter always activates a visible row.
    const flat = flattenResults(applyScope(results, scope))
    if (flat.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = flat[activeIndex]
      if (target) handleSelect(target.href)
    }
  }

  if (!open) return null

  const showHint = query.length < MIN_QUERY_LENGTH
  // Scope-aware flat list keeps the EmptyState in sync with the chip
  // selection: switching to a chip whose bucket has no rows surfaces
  // "No results" instead of silently showing nothing.
  const flat = flattenResults(applyScope(results, scope))
  const showEmpty =
    !showHint && results !== null && flat.length === 0
  const canSave = query.length >= MIN_QUERY_LENGTH
  const savedItems = saved.items

  return (
    <div
      data-testid="search-palette"
      className="fixed inset-0 z-50 flex items-start justify-center pt-2 lg:pt-24"
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
    >
      <button
        type="button"
        aria-label="Close search"
        className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] transition-colors"
        onClick={close}
        tabIndex={-1}
      />
      <div
        data-testid="search-palette-panel"
        className="relative z-10 w-full mx-2 lg:mx-auto lg:max-w-2xl rounded-2xl bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/5 overflow-hidden"
      >
        {/* Phase-2 Batch C2 — result-type filter chips. */}
        <div
          data-testid="search-scope-chips"
          role="tablist"
          aria-label="Filter results by type"
          className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-3"
        >
          {SCOPE_CHIPS.map((chip) => {
            const active = scope === chip.scope
            return (
              <button
                type="button"
                key={chip.scope}
                role="tab"
                aria-selected={active}
                data-testid={`search-scope-chip-${chip.scope}`}
                className={[
                  'rounded-full px-3 py-1 text-xs font-medium transition-all duration-150',
                  active
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800',
                ].join(' ')}
                onMouseDown={(e) => {
                  // mousedown — keeps the input focused (matches the
                  // saved-search chip + result-row patterns).
                  e.preventDefault()
                  setScope(chip.scope)
                  setActiveIndex(0)
                }}
              >
                {chip.label}
              </button>
            )
          })}
        </div>
        {savedItems.length > 0 && (
          <div
            data-testid="saved-searches-strip"
            className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5"
          >
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              Saved
            </span>
            {savedItems.map((s) => (
              <span
                key={s.id}
                data-testid={`saved-search-chip-${s.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs text-slate-700 ring-1 ring-slate-200 transition-shadow hover:ring-slate-300"
              >
                <button
                  type="button"
                  className="hover:text-slate-900 transition-colors"
                  onMouseDown={(e) => {
                    // mousedown fires before blur close on the chip; matches
                    // the row-select pattern in ResultSections.
                    e.preventDefault()
                    loadSaved(s.queryString)
                  }}
                  aria-label={`Load saved search ${s.name}`}
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  data-testid={`saved-search-delete-${s.id}`}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    void handleDeleteSaved(s.id)
                  }}
                  aria-label={`Delete saved search ${s.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3.5">
          <i className="pi pi-search text-slate-400 text-sm" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Search conversations, contacts, calls, recordings, agents..."
            aria-label="Search"
            className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          {canSave && (
            <button
              type="button"
              data-testid="save-search-button"
              className="shrink-0 rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100 hover:text-slate-800 transition-all"
              onMouseDown={(e) => {
                e.preventDefault()
                void handleSave()
              }}
              aria-label="Save this search"
            >
              Save
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {showHint && (
            <div className="px-4 py-10 text-center text-sm text-slate-400">
              Type at least 2 characters
            </div>
          )}
          {showEmpty && (
            <EmptyState
              title="No results"
              description="Try a different search term."
              icon="pi pi-search"
            />
          )}
          {!showHint && !showEmpty && results !== null && (
            <ResultSections
              results={results}
              activeIndex={activeIndex}
              onSelect={handleSelect}
              scope={scope}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchPalette
